/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useDataGrid.ts
 🗓️ Created: 2025-01-05
 🧩 Description:
    Custom hook for managing data grid state, pagination, sorting,
    filtering, and data fetching with comprehensive grid functionality.

 🔧 Notes:
    - Implements complete data grid state management
    - Supports pagination, sorting, and filtering functionality
    - Handles data fetching with loading and error states
    - Integrates with useColumnPreferences for column management
    - Provides search functionality and data refresh capabilities
    - Supports bulk operations and row selection management

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { useColumnPreferences } from './useColumnPreferences';

export interface DataGridColumn<T = unknown> {
  key: string;
  label: string;
  visible: boolean;
  sortable?: boolean;
  render?: (value: unknown, item: T) => ReactNode;
}

export interface DataGridFetchResult {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
  pagination?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
  };
  [key: string]: unknown;
}

interface UseDataGridProps {
  fetchData: (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => Promise<DataGridFetchResult>;
  initialColumns: DataGridColumn[];
  initialSortBy?: string;
  initialSortOrder?: 'asc' | 'desc';
  initialLimit?: number;
  moduleName?: string; // Module name for column preferences
  reloadKey?: string | number | boolean | null; // External key to force reloads
}

export function useDataGrid({
  fetchData,
  initialColumns,
  initialSortBy = 'createdAt',
  initialSortOrder = 'desc',
  initialLimit = 10,
  moduleName,
  reloadKey = null
}: UseDataGridProps) {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [localColumns, setLocalColumns] = useState(initialColumns);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: initialLimit,
    total: 0,
    totalPages: 0
  });
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fetchDataRef = useRef(fetchData);
  const columnsInitializedRef = useRef(false);
  
  // Track current load request to prevent duplicate calls
  const currentLoadKeyRef = useRef<string | null>(null);
  const isLoadingRef = useRef(false);

  // Use column preferences hook if moduleName is provided
  const columnPreferences = moduleName ? useColumnPreferences({
    moduleName,
    defaultColumns: initialColumns
  }) : null;


  // Update fetchDataRef when fetchData changes
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  // Update local columns when initialColumns changes (only if not using column preferences and not initialized)
  useEffect(() => {
    if (!columnPreferences && !columnsInitializedRef.current) {
      setLocalColumns(initialColumns);
      columnsInitializedRef.current = true;
    }
  }, [initialColumns, columnPreferences]);


  // Handle column toggle (fallback for when moduleName is not provided)
  const handleColumnToggleFallback = useCallback((columnKey: string) => {
    // This is only used when moduleName is not provided
    // We need to maintain local state for columns in this case
    setLocalColumns(prev => 
      prev.map(col => 
        col.key === columnKey 
          ? { ...col, visible: !col.visible }
          : col
      )
    );
  }, []);

  // Use columns from preferences or fallback to local state
  const columns = columnPreferences ? columnPreferences.columns : localColumns;
  const handleColumnToggle = columnPreferences ? columnPreferences.toggleColumn : handleColumnToggleFallback;

  // Load data
  const loadData = useCallback(async (searchTerm?: string, pageOverride?: number) => {
    try {
      const currentPage = pageOverride !== undefined ? pageOverride : pagination.page;
      
      // Create a unique key for this load request
      const loadKey = `${currentPage}-${pagination.limit}-${sortBy}-${sortOrder}-${searchTerm || ''}-${String(reloadKey)}`;
      
      // Prevent duplicate calls - if we're already loading this exact request, skip
      if (isLoadingRef.current && currentLoadKeyRef.current === loadKey) {
        return;
      }
      
      currentLoadKeyRef.current = loadKey;
      isLoadingRef.current = true;
      setLoading(true);
      
      const result = await fetchDataRef.current({
        page: currentPage,
        limit: pagination.limit,
        sortBy,
        sortOrder,
        search: searchTerm || undefined
      });
      
      setData(result.data || []);
      setPagination(prev => ({
        ...prev,
        ...result.pagination
      }));
    } catch (error) {
      console.error('useDataGrid: Error loading data:', error);
      setData([]);
      currentLoadKeyRef.current = null; // Reset on error
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [pagination.page, pagination.limit, sortBy, sortOrder, reloadKey]);

  // Load data when dependencies change (using ref to avoid loop)
  const loadDataRef = useRef(loadData);
  loadDataRef.current = loadData;

  useEffect(() => {
    // Only load if we have a valid fetchData function
    if (fetchDataRef.current) {
      loadDataRef.current(searchQuery);
    }
  }, [searchQuery, pagination.page, pagination.limit, sortBy, sortOrder, reloadKey]);

  // Handle search with debounce
  const handleSearch = (query: string) => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Only search if query has at least 2 characters or is empty (reset)
    // This prevents unnecessary API calls on single character
    if (query.length === 1) {
      return; // Ignore single character searches
    }
    
    // Set new timeout for debounced search
    // Longer delay for better UX with large datasets
    debounceTimeoutRef.current = setTimeout(() => {
      setSearchQuery(query);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 800); // 800ms debounce (increased from 300ms for large datasets)
  };

  // Handle sort
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  // Handle items per page change
  const handleItemsPerPageChange = (limit: number) => {
    setPagination(prev => ({ ...prev, page: 1, limit }));
  };

  // Refresh data - memoized to prevent loops
  const refresh = useCallback(async () => {
    // Small delay to ensure backend operation is complete
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Use refs to avoid stale closures and prevent loops
    const currentDataLength = data.length;
    const currentPage = pagination.page;
    const currentSearchQuery = searchQuery;
    
    // Check if current page will be empty after deletion
    if (currentDataLength === 1 && currentPage > 1) {
      // Go to previous page if current page will be empty
      setPagination(prev => ({ ...prev, page: prev.page - 1 }));
      await loadDataRef.current(currentSearchQuery, currentPage - 1);
    } else {
      // Just refresh current page
      await loadDataRef.current(currentSearchQuery);
    }
  }, [data.length, pagination.page, searchQuery]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    // Data
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    
    // Actions
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    
    // Column preferences (only available when moduleName is provided)
    ...(moduleName && columnPreferences && {
      columnPreferences: {
        isLoading: columnPreferences.isLoading,
        error: columnPreferences.error,
        saveColumnPreferences: columnPreferences.saveColumnPreferences,
        resetColumnPreferences: columnPreferences.resetColumnPreferences,
        loadColumnPreferences: columnPreferences.loadColumnPreferences
      }
    })
  };
}
